import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { PROCESS_TYPE } from 'features/sendouts/constants/constants';
import { createSendout } from 'features/sendouts/services/createSendout';
import { createSendoutInterview } from 'features/sendouts/services/createSendoutInterview';
import { updateEntityStatus } from 'features/sendouts/services/entityStatus';
import { updateSendoutDetails } from 'features/sendouts/services/updateSendoutDetails';
import API from 'services/API';
import strings from 'strings';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage } from 'UI/utils';

import { SWITCH_SENDOVER_TO_SENDOUT_TYPES } from '../drawers/ConvertSendoverToSendoutDrawer/ConvertSendoverToSendoutDrawer.constants';

import { DRAWER_TYPES } from './DrawerStack.types';

const patchLegacyConvertSendoverToSendoutPayload = ({ meta, payload }) => {
  const nextPayload = payload?.payload || {};
  const legacyBccEmails = payload?.legacyCompatibility?.bccEmails || [];
  const isWithoutMailFlow =
    meta?.props?.switchType === SWITCH_SENDOVER_TO_SENDOUT_TYPES.WithoutMail;

  if (!isWithoutMailFlow) return nextPayload;

  return {
    ...nextPayload,
    // Legacy API requires explicit cc/bcc arrays, subject and template for the without-mail flow.
    bcc_emails: legacyBccEmails,
    cc_emails: [],
    subject: 'No Email',
    template: '\n   <p>No Template</p> '
  };
};

export const SUBMIT_CONFIG = {
  [DRAWER_TYPES.CallEditor]: {
    handler: async () => ({})
  },
  [DRAWER_TYPES.CreateSendout]: {
    handler: async ({ meta, payload }) => ({
      ...(await createSendout({ jobOrderId: meta?.props?.jobOrderId, payload: payload?.payload })),
      effects: { refreshTable: true }
    })
  },
  [DRAWER_TYPES.CreateSendover]: {
    handler: async ({ meta, payload }) => {
      try {
        const endpoint = Endpoints.JobOrderSendovers.replace(':id', meta?.props?.jobOrderId);
        const response = await API.post(endpoint, payload?.payload);

        if (response.status !== HTTPStatusCodes.Created) {
          return {
            success: false,
            response,
            alert: {
              severity: 'error',
              title: strings.formatString(strings.sendoutsV2.alerts.submitMessages.error.title, {
                processType: PROCESS_TYPE.Sendover.title,
                action: 'Creating'
              }),
              autoHideDuration: 5000,
              body: `Unexpected response status: ${response.status}`
            }
          };
        }

        return {
          success: true,
          response,
          effects: {
            refreshTable: true
          },
          alert: {
            severity: 'success',
            title: strings.formatString(strings.sendoutsV2.alerts.submitMessages.success.title, {
              processType: PROCESS_TYPE.Sendover.title,
              action: 'Created'
            }),
            autoHideDuration: 5000
          }
        };
      } catch (error) {
        return {
          success: false,
          error,
          alert: {
            severity: 'error',
            title: strings.formatString(strings.sendoutsV2.alerts.submitMessages.error.title, {
              processType: PROCESS_TYPE.Sendover.title,
              action: 'Creating'
            }),
            autoHideDuration: 5000,
            body: getErrorMessage(error, error?.message ?? strings.shared.ui.unknownError.title)
          }
        };
      }
    }
  },
  [DRAWER_TYPES.ConvertSendoverToSendout]: {
    handler: async ({ meta, payload }) => {
      try {
        const endpoint = Endpoints.SendoverConvert.replace(':id', meta?.props?.sendover?.id);
        const response = await API.put(
          endpoint,
          patchLegacyConvertSendoverToSendoutPayload({ meta, payload })
        );
        const isSuccessfulConvertResponse =
          response.status === HTTPStatusCodes.Ok || response.status === HTTPStatusCodes.Created;

        if (!isSuccessfulConvertResponse) {
          return {
            success: false,
            response,
            alert: {
              severity: 'error',
              title: strings.formatString(strings.sendoutsV2.alerts.submitMessages.error.title, {
                processType: PROCESS_TYPE.Sendout.title
              }),
              autoHideDuration: 5000,
              body: `Unexpected response status: ${response.status}`
            }
          };
        }

        return {
          success: true,
          response,
          effects: {
            refreshTable: true
          },
          alert: {
            severity: 'success',
            title: strings.formatString(strings.sendoutsV2.alerts.submitMessages.success.title, {
              processType: PROCESS_TYPE.Sendout.title,
              action: 'Created'
            }),
            autoHideDuration: 5000
          }
        };
      } catch (error) {
        return {
          success: false,
          error,
          alert: {
            severity: 'error',
            title: strings.formatString(strings.sendoutsV2.alerts.submitMessages.error.title, {
              processType: PROCESS_TYPE.Sendout.title,
              action: 'Creating'
            }),
            autoHideDuration: 5000,
            body: getErrorMessage(error, error?.message ?? strings.shared.ui.unknownError.title)
          }
        };
      }
    }
  },
  [DRAWER_TYPES.SendoverDeclinationReasons]: {
    handler: async ({ meta, payload }) => ({
      ...(await updateEntityStatus({
        id: meta?.props?.sendoverId,
        payload: payload?.payload,
        processType: PROCESS_TYPE.Sendover.title
      })),
      effects: {
        refreshTable: true
      }
    })
  },
  [DRAWER_TYPES.SendoutDeclinationReasons]: {
    handler: async ({ meta, payload }) => ({
      ...(await updateEntityStatus({
        id: meta?.props?.sendoutId,
        payload: payload?.payload,
        processType: PROCESS_TYPE.Sendout.title
      })),
      effects: {
        refreshTable: true
      }
    })
  },
  [DRAWER_TYPES.SendoutDetailsEdit]: {
    handler: async ({ meta, payload }) => ({
      ...(await updateSendoutDetails({
        sendoutId: meta?.props?.sendoutId,
        payload: payload?.payload
      })),
      effects: {
        refreshTable: true
      }
    })
  },
  [DRAWER_TYPES.CreateSendoutInterview]: {
    handler: async ({ meta, payload }) => ({
      ...(await createSendoutInterview({
        sendoutId: meta?.props?.sendoutId,
        payload: payload?.payload
      })),
      effects: {
        refreshTable: true
      }
    })
  }
};
